'use client';
'use strict';

var _tslib = require('../../../_virtual/_tslib.js');
var jsxRuntime = require('react/jsx-runtime');
var react = require('react');
var tailwindMerge = require('tailwind-merge');
var Typography = require('../../Base/Typography/Typography.js');
var Box = require('../../Base/Layouts/Box/Box.js');
require('../../Base/Layouts/FullBleed/FullBleed.js');

const MultiColumnListContext = react.createContext({
    activeCategory: 0,
    currentActiveCategory: 0,
    handleCategoryClick: ()=>{}
});
/**
 * MultiColumnList의 최상위 컴포넌트입니다.
 *
 * @param {RootProps} props
 * @param {React.ReactNode} props.children - 자식 컴포넌트
 * @param {string} [props.className] - 추가 스타일 클래스
 * @param {number} [props.currentActiveCategory=0] - 현재 활성화된 카테고리 인덱스
 * @param {(index: number) => void} [props.onCategoryChange] - 카테고리 변경 시 호출되는 콜백
 *
 * @example
 * ```tsx
 * <MultiColumnList.Root onCategoryChange={(index) => console.log(index)}>
 *   <MultiColumnList.CategoryContainer>
 *     <MultiColumnList.Category index={0}>카테고리 1</MultiColumnList.Category>
 *     <MultiColumnList.Category index={1}>카테고리 2</MultiColumnList.Category>
 *   </MultiColumnList.CategoryContainer>
 *   <MultiColumnList.ListContainer>
 *     <MultiColumnList.List index={0}>내용 1</MultiColumnList.List>
 *     <MultiColumnList.List index={1}>내용 2</MultiColumnList.List>
 *   </MultiColumnList.ListContainer>
 * </MultiColumnList.Root>
 * ```
 */ const Root = (_a)=>{
    var { children, className, currentActiveCategory = 0, onCategoryChange } = _a, rest = _tslib.__rest(_a, [
        "children",
        "className",
        "currentActiveCategory",
        "onCategoryChange"
    ]);
    const [activeCategory, setActiveCategory] = react.useState(currentActiveCategory);
    const handleCategoryClick = (index)=>{
        if (index === activeCategory) return; // 현재 카테고리를 다시 클릭하는 경우 방지
        onCategoryChange === null || onCategoryChange === void 0 ? void 0 : onCategoryChange(index);
        setActiveCategory(index);
    };
    react.useEffect(()=>{
        if (currentActiveCategory !== activeCategory) {
            setActiveCategory(currentActiveCategory);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        currentActiveCategory
    ]);
    return jsxRuntime.jsx(MultiColumnListContext.Provider, {
        value: {
            activeCategory,
            currentActiveCategory,
            handleCategoryClick
        },
        children: jsxRuntime.jsx(Box, Object.assign({
            "data-testid": "design-system-multi-column-list--root",
            className: tailwindMerge.twMerge('flex h-full w-full rounded-xl border border-w-gray-200 bg-w-white', className)
        }, rest, {
            children: children
        }))
    });
};
/**
 * 카테고리 목록을 포함하는 컨테이너 컴포넌트입니다.
 *
 * @param {RootProps} props
 * @param {React.ReactNode} props.children - Category 컴포넌트들
 * @param {string} [props.className] - 추가 스타일 클래스
 *
 * @example
 * ```tsx
 * <MultiColumnList.CategoryContainer>
 *   <MultiColumnList.Category index={0}>카테고리 1</MultiColumnList.Category>
 *   <MultiColumnList.Category index={1}>카테고리 2</MultiColumnList.Category>
 * </MultiColumnList.CategoryContainer>
 * ```
 */ const CategoryContainer = (_a)=>{
    var { className, children } = _a, rest = _tslib.__rest(_a, [
        "className",
        "children"
    ]);
    return jsxRuntime.jsx(Box, Object.assign({
        "data-testid": "design-system-multi-column-list--category-container",
        className: tailwindMerge.twMerge('flex h-full w-[180px] shrink-0 flex-col overflow-y-auto border-r border-w-gray-200 py-2 pl-2 scrollbar-list', className)
    }, rest, {
        children: children
    }));
};
/**
 * 선택된 카테고리의 내용을 표시하는 컨테이너 컴포넌트입니다.
 *
 * @param {RootProps} props
 * @param {React.ReactNode} props.children - List 컴포넌트들
 * @param {string} [props.className] - 추가 스타일 클래스
 *
 * @example
 * ```tsx
 * <MultiColumnList.ListContainer>
 *   <MultiColumnList.List index={0}>내용 1</MultiColumnList.List>
 *   <MultiColumnList.List index={1}>내용 2</MultiColumnList.List>
 * </MultiColumnList.ListContainer>
 * ```
 */ const ListContainer = (_a)=>{
    var { children, className } = _a, rest = _tslib.__rest(_a, [
        "children",
        "className"
    ]);
    const { activeCategory } = react.useContext(MultiColumnListContext);
    const ref = react.useRef(null);
    react.useEffect(()=>{
        if (ref.current) {
            ref.current.scrollTo({
                top: 0
            });
        }
    }, [
        activeCategory
    ]);
    return jsxRuntime.jsx(Box, Object.assign({
        "data-testid": "design-system-multi-column-list--list-container",
        ref: ref,
        className: tailwindMerge.twMerge('flex h-full w-full flex-col gap-6 overflow-y-auto px-6 py-5 scrollbar-list', className)
    }, rest, {
        children: children
    }));
};
/**
 * 개별 카테고리 항목 컴포넌트입니다.
 *
 * @param {CategoryProps} props
 * @param {React.ReactNode} props.children - 카테고리 텍스트
 * @param {string} [props.className] - 추가 스타일 클래스
 * @param {number} props.index - 카테고리 인덱스
 *
 * @example
 * ```tsx
 * <MultiColumnList.Category index={0}>카테고리 1</MultiColumnList.Category>
 * ```
 */ const Category = (_a)=>{
    var { children, className, index } = _a, rest = _tslib.__rest(_a, [
        "children",
        "className",
        "index"
    ]);
    const { handleCategoryClick, activeCategory } = react.useContext(MultiColumnListContext);
    const isSelected = index === activeCategory;
    return jsxRuntime.jsx(Box, Object.assign({
        "data-testid": "design-system-multi-column-list--category",
        className: tailwindMerge.twMerge('w-full cursor-pointer rounded-xl px-4 py-3 text-w-gray-600 hover:bg-primary-10', isSelected && 'font-medium text-primary', className),
        onClick: ()=>index !== undefined && handleCategoryClick(index)
    }, rest, {
        children: jsxRuntime.jsx(Typography, {
            variant: "body1",
            className: "whitespace-normal break-all",
            children: children
        })
    }));
};
/**
 * 카테고리에 해당하는 내용을 표시하는 컴포넌트입니다.
 *
 * @param {ListProps} props
 * @param {React.ReactNode} props.children - 리스트 내용
 * @param {string} [props.title] - 리스트 제목
 * @param {number} props.index - 리스트 인덱스 (해당 카테고리 인덱스와 일치해야 표시됨)
 *
 * @example
 * ```tsx
 * <MultiColumnList.List index={0} title="섹션 제목">
 *   <div>리스트 내용</div>
 * </MultiColumnList.List>
 * ```
 */ const List = (_a)=>{
    var { children, title, index } = _a, rest = _tslib.__rest(_a, [
        "children",
        "title",
        "index"
    ]);
    const { activeCategory } = react.useContext(MultiColumnListContext);
    if (index !== activeCategory) return null;
    return jsxRuntime.jsxs(Box, Object.assign({
        "data-testid": "design-system-multi-column-list--list-item",
        className: "flex w-full flex-col gap-4"
    }, rest, {
        children: [
            title && jsxRuntime.jsx(Typography, {
                variant: "body1",
                className: "w-full font-medium text-primary",
                "data-testid": "design-system-multi-column-list-title",
                children: title
            }),
            children
        ]
    }));
};

exports.Category = Category;
exports.CategoryContainer = CategoryContainer;
exports.List = List;
exports.ListContainer = ListContainer;
exports.Root = Root;
